/**
 * AtlasInventoryModule — the Provenance System inventory manager (Passport tab).
 *
 * Lists the local player's inventory items with their RESOLVED permission flags
 * (Copy / Modify / Transfer / Remix / Template, plus premium), and offers the
 * per-asset actions (rez into your world, remix, instantiate template) plus a
 * provenance "i" that opens the lineage browser. The client only ever displays
 * what the server's public tables expose — every action is server-validated.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { MARKET_TAX } from '../config.js';
import type { Asset, InventoryItem } from '@shared/types/inventory.js';
import { groundYIn } from '../lib/grounding.js';

export class AtlasInventoryModule implements IModule {
  readonly name = 'AtlasInventory';

  private list!: HTMLElement;
  private note!: HTMLElement;
  private createBtn!: HTMLButtonElement;

  init(_app: App): void {
    this.list = document.getElementById('inv-list')!;
    this.note = document.getElementById('inv-note')!;
    this.createBtn = document.getElementById('inv-create') as HTMLButtonElement;

    this.createBtn.addEventListener('click', () => this.createTestAsset());
    bus.on('atlas:inventory-updated', () => this.render());
  }

  private render(): void {
    const items = [...atlas.inventory.values()].sort((a, b) => Number(a.itemId - b.itemId));
    if (items.length === 0) {
      this.list.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = 'No items yet.';
      this.list.appendChild(empty);
      return;
    }
    this.list.innerHTML = '';
    for (const item of items) {
      const asset = atlas.assets.get(item.assetId.toString());
      if (!asset) continue;
      this.list.appendChild(this.renderItem(item, asset));
    }
  }

  private permSpan(label: string, on: boolean): string {
    return `<span class="${on ? 'perm-on' : 'perm-off'}">${on ? label : label.toLowerCase()}</span>`;
  }

  private renderItem(item: InventoryItem, asset: Asset): HTMLElement {
    const el = document.createElement('div');
    el.className = 'inv-item';

    const premium = asset.isPremium ? ' <span class="premium">★ premium</span>' : '';
    const lineage = asset.parentAssetId !== 0n ? ` ⤳ remix of #${asset.parentAssetId}` : '';
    const perms =
      this.permSpan('C', asset.permCopy) + ' ' +
      this.permSpan('M', asset.permModify) + ' ' +
      this.permSpan('T', asset.permTransfer) + ' ' +
      this.permSpan('R', asset.permRemix) + ' ' +
      this.permSpan('Tpl', asset.permTemplate);
    const nextOwner = `next-owner: ${item.nextOwnerCopy ? 'C' : '·'}${item.nextOwnerModify ? 'M' : '·'}${item.nextOwnerTransfer ? 'T' : '·'}`;

    el.innerHTML =
      `<div class="head"><span class="kind">${asset.kind} #${asset.assetId}</span>` +
      `<button class="prov" data-prov="${asset.assetId}">ⓘ provenance</button></div>` +
      `<div class="perms">${perms}${premium}${lineage}</div>` +
      `<div class="perms">${nextOwner} · creator ${asset.creator.slice(0, 8)}…</div>`;

    // Actions.
    const actions = document.createElement('div');
    actions.className = 'actions';
    const rezBtn = this.actionBtn('Rez here', () => this.rez(asset.assetId));
    actions.appendChild(rezBtn);
    if (asset.permRemix) {
      actions.appendChild(this.actionBtn('Remix', () => this.remix(asset.assetId)));
    }
    if (asset.permTemplate) {
      actions.appendChild(this.actionBtn('Instantiate', () => this.instantiate(asset.assetId)));
    }
    // Transferable assets can be listed for fixed-price sale on the marketplace.
    if (asset.permTransfer) {
      actions.appendChild(this.actionBtn('Sell', () => this.sell(asset.assetId)));
    }
    el.appendChild(actions);

    el.querySelector<HTMLButtonElement>('[data-prov]')?.addEventListener('click', () => {
      bus.emit('atlas:show-lineage', { assetId: asset.assetId });
    });
    return el;
  }

  private actionBtn(label: string, fn: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.addEventListener('click', fn);
    return b;
  }

  // ─── Reducer calls ────────────────────────────────────────────────────────────

  private createTestAsset(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Creating asset…', '');
    void conn.reducers
      .createAsset({
        kind: 'furniture',
        contentRef: `minio://test-${Date.now()}.glb`,
        permCopy: true,
        permModify: true,
        permTransfer: true,
        permRemix: true,
        permTemplate: false,
        isPremium: false,
        // A small test mesh: ~5k polys + ~2 MiB of texture → a modest upload fee.
        polyCount: 5_000n,
        textureBytes: 2_097_152n,
      })
      .then(() => this.setNote('Asset created.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private rez(assetId: bigint): void {
    const conn = atlas.conn;
    if (!conn || atlas.personalWorldId === null) {
      this.setNote('Enter your own world to rez (Tab).', 'err');
      return;
    }
    const fx = atlas.localX - Math.sin(atlas.localYaw) * 1.5;
    const fz = atlas.localZ - Math.cos(atlas.localYaw) * 1.5;
    void conn.reducers
      .rezObject({
        worldId: atlas.personalWorldId,
        sourceAssetId: assetId,
        posX: fx,
        // Seat on the rendered ground (high-relief audit 2026-08-05) — a
        // constant 0.5 buried/floated items on any non-flat terrain.
        posY: groundYIn(conn, atlas.personalWorldId, fx, fz) + 0.5,
        posZ: fz,
        rotX: 0,
        rotY: 0,
        rotZ: 0,
        rotW: 1,
        scale: 1,
      })
      .then(() => this.setNote(`Rezzed asset #${assetId}.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private remix(assetId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .remixAsset({ sourceAssetId: assetId })
      .then(() => this.setNote(`Remixed asset #${assetId}.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private instantiate(assetId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .instantiateTemplate({ sourceAssetId: assetId })
      .then(() => this.setNote(`Instantiated template #${assetId}.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  /** List a transferable asset for fixed-price sale (prompts for a SOV price). */
  private sell(assetId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const raw = window.prompt(`List asset #${assetId} for sale.\nPrice in Sovereigns (SOV):`, '100');
    if (raw === null) return; // cancelled
    let price: bigint;
    try {
      price = BigInt(raw.trim());
    } catch {
      this.setNote('Enter a whole number of Sovereigns.', 'err');
      return;
    }
    if (price <= 0n) {
      this.setNote('Price must be a positive whole number.', 'err');
      return;
    }
    // Show the seller their net proceeds after the marketplace tax (display only;
    // the server computes the authoritative split in bigint basis points).
    const net = price - (price * BigInt(Math.round(MARKET_TAX * 10_000))) / 10_000n;
    this.setNote(`Listing #${assetId} for ${price} SOV (you net ~${net} after tax)…`, '');
    void conn.reducers
      .listAsset({ assetId, priceSov: price })
      .then(() => this.setNote(`Listed asset #${assetId} for ${price} SOV.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err'): void {
    this.note.className = cls ? `note ${cls}` : 'note';
    this.note.textContent = text;
  }
}
